import type { JSONValue } from 'ai';

/**
 * Pure memo comparator for `AssistantMessage`, extracted so it unit-tests without
 * pulling in the heavy component (react-markdown, Shiki, …).
 *
 * WHY THIS EXISTS: the parent (`Messages`) rebuilds the `annotations` array on
 * every render (a spread of legacy + `data-*` part annotations), so the default
 * shallow `memo` was always defeated — EVERY message re-rendered on EVERY
 * streaming tick, running react-markdown + Shiki N× per token and freezing the
 * main thread. This comparator inspects only the props that change what a message
 * renders, so unchanged messages skip re-render entirely during streaming.
 */

/** The subset of AssistantMessage props that affect the rendered output. */
export interface AssistantMemoProps {
  content?: string;
  parts?: any[];
  annotations?: JSONValue[];
  metadata?: Record<string, any>;
  messageId?: string;
  isLastMessage?: boolean;
  isStreaming?: boolean;
  hasCheckpoint?: boolean;
  hideMemoryRecall?: boolean;
  chatMode?: 'discuss' | 'build';
  model?: string;
  provider?: unknown;
}

/**
 * Shallow-equal two annotation arrays by ELEMENT reference. The wrapper array is
 * a fresh reference every render, but the ITEMS are stable (they come from
 * `message.annotations` / part `.data`, unchanged for an unchanged message), so
 * comparing by element treats "same content, new wrapper" as equal.
 */
export function sameAnnotationRefs(a?: JSONValue[], b?: JSONValue[]): boolean {
  if (a === b) {
    return true;
  }

  if (!a || !b || a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }

  return true;
}

/**
 * True when two prop sets render identically (so `memo` can skip the re-render).
 *
 * Compares the render-affecting props: `annotations` by element (above),
 * everything else by ref/value. During streaming only the tail message's `parts`
 * reference (and its `content`) changes, so historical messages compare equal and
 * are skipped.
 *
 * The operand-driven callbacks (`onRestore`/`onFork`/`append`/`setChatMode` —
 * each takes its target as an argument; none captures per-message state) are
 * intentionally NOT compared: they never change what a message renders, and
 * skipping them is what lets the memo actually hold when the parent passes fresh
 * callback identities.
 */
export function assistantMessagePropsEqual(prev: AssistantMemoProps, next: AssistantMemoProps): boolean {
  return (
    prev.messageId === next.messageId &&
    prev.content === next.content &&
    prev.parts === next.parts &&
    prev.metadata === next.metadata &&
    prev.isLastMessage === next.isLastMessage &&
    prev.isStreaming === next.isStreaming &&
    prev.hasCheckpoint === next.hasCheckpoint &&
    prev.hideMemoryRecall === next.hideMemoryRecall &&
    prev.chatMode === next.chatMode &&
    prev.model === next.model &&
    prev.provider === next.provider &&
    sameAnnotationRefs(prev.annotations, next.annotations)
  );
}
